import React, { useEffect, useState, useRef, Fragment } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Condition, ConditionGroup, Document, ConditionStatus, NoteHistoryEntry, CommentVisibility } from '../types';
import { ConditionRow } from './ConditionRow';
import { FolderIcon, MessageSquareIcon, ArrowRightIcon, XIcon, PencilIcon, CheckIcon, ClockIcon } from 'lucide-react';
interface ConditionTableProps {
  conditions: Condition[];
  groups: ConditionGroup[];
  documents: Document[];
  onStatusChange: (conditionId: string, status: ConditionStatus) => void;
  onRemove: (conditionId: string) => void;
  onCommentGroup?: (conditionIds: string[], comment: string) => void;
  onMoveToGroup?: (conditionIds: string[], groupId: string | undefined) => void;
  onUpdateNote?: (groupId: string, newNote: string) => void;
  onAddComment?: (conditionId: string, text: string, visibility: CommentVisibility) => void;
  showNoteHistory?: boolean;
}
export function ConditionTable({
  conditions,
  groups,
  documents,
  onStatusChange,
  onRemove,
  onCommentGroup,
  onMoveToGroup,
  onUpdateNote,
  onAddComment,
  showNoteHistory
}: ConditionTableProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [commentText, setCommentText] = useState('');
  const [moveDropdownOpen, setMoveDropdownOpen] = useState(false);
  const moveDropdownRef = useRef<HTMLDivElement>(null);
  const [editingBubbleId, setEditingBubbleId] = useState<string | null>(null);
  const [editBubbleText, setEditBubbleText] = useState('');
  const editBubbleRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editingBubbleId && editBubbleRef.current) {
      editBubbleRef.current.focus();
      editBubbleRef.current.select();
    }
  }, [editingBubbleId]);
  const startBubbleEdit = (group: ConditionGroup) => {
    setEditingBubbleId(group.id);
    setEditBubbleText(group.note || group.name);
  };
  const saveBubbleEdit = () => {
    if (editingBubbleId && onUpdateNote && editBubbleText.trim()) {
      onUpdateNote(editingBubbleId, editBubbleText.trim());
    }
    setEditingBubbleId(null);
    setEditBubbleText('');
  };
  const cancelBubbleEdit = () => {
    setEditingBubbleId(null);
    setEditBubbleText('');
  };
  useEffect(() => {
    if (!moveDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (moveDropdownRef.current && !moveDropdownRef.current.contains(e.target as Node)) {
        setMoveDropdownOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoveDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [moveDropdownOpen]);
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const clearSelection = () => {
    setSelectedIds(new Set());
    setCommentText('');
    setMoveDropdownOpen(false);
  };
  const handleSubmit = () => {
    if (commentText.trim() && selectedIds.size > 0 && onCommentGroup) {
      onCommentGroup(Array.from(selectedIds), commentText.trim());
      clearSelection();
    }
  };
  const handleMoveTo = (groupId: string | undefined) => {
    if (selectedIds.size > 0 && onMoveToGroup) {
      onMoveToGroup(Array.from(selectedIds), groupId);
      clearSelection();
    }
  };
  if (conditions.length === 0) {
    return <div className="py-8 text-center text-slate-500 text-sm bg-white border border-slate-200 rounded-lg" data-id="element-216">
        No conditions found.
      </div>;
  }
  // Organize: ungrouped first, then by group
  const ungrouped = conditions.filter(c => !c.groupId);
  const groupedSections = groups.map(group => ({
    group,
    conditions: conditions.filter(c => c.groupId === group.id)
  })).filter(section => section.conditions.length > 0);
  const renderRow = (condition: Condition) => {
    const group = condition.groupId ? groups.find(g => g.id === condition.groupId) : undefined;
    return <ConditionRow key={condition.id} condition={condition} linkedDocument={documents.find(d => d.conditionIds.includes(condition.id))} isSelected={selectedIds.has(condition.id)} groupName={group ? group.note || group.name : undefined} onToggleSelect={() => toggleSelect(condition.id)} onStatusChange={status => onStatusChange(condition.id, status)} onRemoveFromGroup={group && onMoveToGroup ? () => onMoveToGroup([condition.id], undefined) : undefined} onRemove={() => onRemove(condition.id)} onAddComment={onAddComment} data-id="element-217" />;
  };
  return <>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-id="element-218">
        {/* Table Header */}
        <div className="flex items-center py-2.5 px-4 bg-slate-600 text-white text-xs font-semibold uppercase tracking-wider" data-id="element-219">
          <div className="w-8 flex-shrink-0" data-id="element-220"></div>
          <div className="flex-1 min-w-0 pr-4" data-id="element-221">Name</div>
          <div className="w-28 flex-shrink-0 text-center" data-id="element-222">Status</div>
          <div className="w-24 flex-shrink-0 text-center" data-id="element-223">Created</div>
          <div className="w-24 flex-shrink-0 text-center" data-id="element-224">Requested</div>
          <div className="w-32 flex-shrink-0 text-center" data-id="element-225">Assigned To</div>
          <div className="w-56 flex-shrink-0 text-center pr-2" data-id="element-226">Action</div>
        </div>

        <div className="divide-y divide-slate-100" data-id="element-227">
          <AnimatePresence mode="popLayout" data-id="element-228">
            {ungrouped.map(renderRow)}

            {groupedSections.map(({
            group,
            conditions: groupConditions
          }) => <Fragment key={group.id} data-id="element-229">
                {/* Group container with visible border */}
                <div style={{
              border: '2px solid #93c5fd',
              borderRadius: '8px',
              margin: '8px 8px',
              overflow: 'hidden',
              backgroundColor: '#ffffff'
            }} data-id="element-230">
                  {/* Inline group comment header */}
                  <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} style={{
                backgroundColor: 'transparent'
              }} className="flex items-center px-4 py-2.5" data-id="element-231">
                    <div className="flex items-center gap-2 w-full" data-id="element-232">
                      <div style={{
                    backgroundColor: '#e2e8f0'
                  }} className="h-px flex-1" data-id="element-233" />
                      {editingBubbleId === group.id ? <div className="inline-flex items-center gap-2" data-id="element-234">
                          <input ref={editBubbleRef} type="text" value={editBubbleText} onChange={e => setEditBubbleText(e.target.value)} onKeyDown={e => {
                      if (e.key === 'Enter') saveBubbleEdit();
                      if (e.key === 'Escape') cancelBubbleEdit();
                    }} onBlur={saveBubbleEdit} className="px-3.5 py-1.5 text-sm font-semibold rounded-full border focus:outline-none focus:ring-2 focus:ring-blue-400" style={{
                      backgroundColor: '#ffffff',
                      borderColor: '#3b82f6',
                      color: '#1e40af',
                      minWidth: '120px'
                    }} data-id="element-235" />
                          <button onMouseDown={e => e.preventDefault()} onClick={saveBubbleEdit} className="w-7 h-7 rounded-full flex items-center justify-center transition-opacity hover:opacity-80" style={{
                      backgroundColor: '#3b82f6',
                      color: '#ffffff'
                    }} data-id="element-236">
                            <CheckIcon className="w-3.5 h-3.5" data-id="element-237" />
                          </button>
                        </div> : <button onClick={() => startBubbleEdit(group)} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 border rounded-full transition-all group/bubble cursor-pointer" style={{
                    backgroundColor: '#dbeafe',
                    borderColor: '#93c5fd',
                    boxShadow: '0 2px 8px rgba(147,197,253,0.3)'
                  }} title="Click to edit" data-id="element-238">
                          <MessageSquareIcon className="w-3.5 h-3.5" style={{
                      color: '#3b82f6'
                    }} data-id="element-239" />
                          <span style={{
                      color: '#1e40af'
                    }} className="text-sm font-semibold" data-id="element-240">
                            {group.note || group.name}
                          </span>
                          <PencilIcon className="w-3 h-3 opacity-0 group-hover/bubble:opacity-100 transition-opacity ml-0.5" style={{
                      color: '#3b82f6'
                    }} data-id="element-241" />
                          <span style={{
                      backgroundColor: '#93c5fd',
                      color: '#1e3a8a'
                    }} className="text-[10px] font-bold ml-1 px-1.5 py-0.5 rounded-full" data-id="element-242">
                            {groupConditions.length}
                          </span>
                        </button>}
                      <div style={{
                    backgroundColor: '#e2e8f0'
                  }} className="h-px flex-1" data-id="element-243" />
                    </div>
                  </motion.div>

                  {/* Inline note history */}
                  <AnimatePresence data-id="element-244">
                    {showNoteHistory && (group.noteHistory ?? []).length > 1 && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} className="overflow-hidden" data-id="element-245">
                          <div className="px-6 py-3" style={{
                    backgroundColor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0'
                  }} data-id="element-246">
                            <div className="max-w-md mx-auto" data-id="element-247">
                              <div className="flex items-center gap-1.5 mb-2" data-id="element-248">
                                <ClockIcon className="w-3 h-3" style={{
                          color: '#94a3b8'
                        }} data-id="element-249" />
                                <span className="text-xs font-medium" style={{
                          color: '#64748b'
                        }} data-id="element-250">
                                  Note history
                                </span>
                              </div>
                              <div className="space-y-0" data-id="element-251">
                                {[...(group.noteHistory ?? [])].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()).map((entry, idx) => {
                          const isLatest = idx === 0;
                          return <div key={idx} className="flex items-start gap-2 py-1.5" style={{
                            borderTop: idx > 0 ? '1px solid #e2e8f0' : 'none'
                          }} data-id="element-252">
                                        <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{
                              backgroundColor: isLatest ? '#3b82f6' : '#cbd5e1'
                            }} data-id="element-253" />
                                        <div className="flex-1 min-w-0" data-id="element-254">
                                          <p className="text-sm" style={{
                                color: isLatest ? '#1e40af' : '#64748b',
                                fontWeight: isLatest ? 600 : 400
                              }} data-id="element-255">
                                            {entry.note}
                                          </p>
                                          <span className="text-xs" style={{
                                color: '#94a3b8'
                              }} data-id="element-256">
                                            {entry.timestamp.toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit'
                                })}
                                            {isLatest && ' · current'}
                                          </span>
                                        </div>
                                      </div>;
                        })}
                              </div>
                            </div>
                          </div>
                        </motion.div>}
                  </AnimatePresence>

                  {groupConditions.map(renderRow)}
                </div>
              </Fragment>)}
          </AnimatePresence>
        </div>
      </div>

      {/* Floating Comment Bar */}
      <AnimatePresence data-id="element-257">
        {selectedIds.size > 0 && <motion.div initial={{
        y: 80,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 80,
        opacity: 0
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-4xl" data-id="element-258">
            <div style={{
          backgroundColor: '#0f172a',
          borderColor: '#334155'
        }} className="rounded-2xl border px-5 py-4 flex items-center gap-4" data-id="element-259">
              <div className="flex items-center gap-3 min-w-[140px]" data-id="element-260">
                <div style={{
              backgroundColor: '#3b82f6',
              boxShadow: '0 4px 14px rgba(59,130,246,0.4)'
            }} className="w-8 h-8 rounded-full text-white flex items-center justify-center text-sm font-bold" data-id="element-261">
                  {selectedIds.size}
                </div>
                <span style={{
              color: '#cbd5e1'
            }} className="text-sm font-medium" data-id="element-262">
                  selected
                </span>
                <button onClick={clearSelection} style={{
              color: '#94a3b8'
            }} className="text-xs hover:opacity-80 transition-opacity underline" data-id="element-263">
                  Clear
                </button>
              </div>

              <div className="flex-1" data-id="element-264">
                <input type="text" value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Leave a comment to group these conditions..." style={{
              backgroundColor: '#1e293b',
              color: '#f1f5f9',
              borderColor: '#475569'
            }} className="w-full px-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" onKeyDown={e => {
              if (e.key === 'Enter') handleSubmit();
            }} data-id="element-265" />
              </div>

              <button onClick={handleSubmit} disabled={!commentText.trim()} style={{
            backgroundColor: '#3b82f6',
            boxShadow: '0 4px 14px rgba(59,130,246,0.35)'
          }} className="px-5 py-2.5 text-sm font-semibold text-white rounded-xl hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity whitespace-nowrap flex items-center gap-2" data-id="element-266">
                <MessageSquareIcon className="w-4 h-4" data-id="element-267" />
                Comment & Group
              </button>

              {/* Move to Group dropdown */}
              {groups.length > 0 && <>
                  <div style={{
              backgroundColor: '#475569'
            }} className="w-px h-8" data-id="element-268" />
                  <div className="relative" ref={moveDropdownRef} data-id="element-269">
                    <button onClick={() => setMoveDropdownOpen(!moveDropdownOpen)} style={{
                backgroundColor: '#334155',
                color: '#cbd5e1'
              }} className="px-4 py-2.5 text-sm font-medium rounded-xl hover:opacity-90 transition-opacity whitespace-nowrap flex items-center gap-2" data-id="element-270">
                      <ArrowRightIcon className="w-4 h-4" data-id="element-271" />
                      Move to...
                    </button>

                    {moveDropdownOpen && <div style={{
                backgroundColor: '#1e293b',
                borderColor: '#334155'
              }} className="absolute bottom-full mb-2 right-0 border rounded-xl py-1 min-w-[200px] z-50" data-id="element-272">
                        <button onClick={() => handleMoveTo(undefined)} style={{
                  color: '#94a3b8'
                }} className="w-full text-left px-3 py-2.5 text-sm flex items-center gap-2.5 hover:opacity-80 transition-opacity" data-id="element-273">
                          <XIcon className="w-3.5 h-3.5" style={{
                    color: '#64748b'
                  }} data-id="element-274" />
                          Ungrouped
                        </button>
                        <div style={{
                  borderColor: '#334155'
                }} className="border-t my-1" data-id="element-275" />
                        {groups.map(group => <button key={group.id} onClick={() => handleMoveTo(group.id)} style={{
                  color: '#cbd5e1'
                }} className="w-full text-left px-3 py-2.5 text-sm flex items-center gap-2.5 hover:opacity-80 transition-opacity" data-id="element-276">
                            <MessageSquareIcon className="w-3.5 h-3.5" style={{
                    color: '#64748b'
                  }} data-id="element-277" />
                            <span className="truncate" data-id="element-278">
                              {group.note || group.name}
                            </span>
                          </button>)}
                      </div>}
                  </div>
                </>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}